/**
 * Date/Time Utility Functions
 * Handles conversion from UTC to Africa/Cairo timezone (UTC+2)
 * Supports both Arabic and English locales
 */

export const CAIRO_TIMEZONE = 'Africa/Cairo';

// Type for translation function
type TranslationFunction = (key: string, options?: any) => string;

/**
 * Convert UTC datetime string to Cairo timezone
 * This is the core function used by all time-related utilities
 * @param utcString - UTC datetime string from backend
 * @returns Date object in Cairo timezone
 */
export function convertUtcToCairo(utcString: string): Date {
  // Ensure UTC parsing by appending 'Z' if not present
  // Backend timestamps without 'Z' are incorrectly parsed as local time
  let isoString = utcString.trim();
  if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
    isoString += 'Z';
  }
  
  const utcDate = new Date(isoString);
  
  // Convert to Cairo timezone by parsing the localized string
  return new Date(
    utcDate.toLocaleString('en-US', {
      timeZone: CAIRO_TIMEZONE,
    })
  );
}

/**
 * Get current time in Cairo timezone
 * @returns Current Date object in Cairo timezone
 */
export function getCurrentCairoTime(): Date {
  return new Date(
    new Date().toLocaleString('en-US', {
      timeZone: CAIRO_TIMEZONE,
    })
  );
}

/**
 * Format UTC datetime to Cairo timezone with localization
 * @param utcString - UTC datetime string from backend
 * @param locale - Current language locale ('ar' or 'en')
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted datetime string in Cairo timezone
 */
export function formatCairoTime(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en',
  options?: Intl.DateTimeFormatOptions
): string {
  // Handle null/undefined/empty cases
  if (!utcString || utcString.trim() === '') {
    return '-';
  }

  try {
    // Ensure UTC parsing by appending 'Z' if not present
    let isoString = utcString.trim();
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString += 'Z';
    }
    
    const date = new Date(isoString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${utcString}`);
      return '-';
    }

    const localeString = locale === 'ar' ? 'ar-EG' : 'en-US';
    
    // Default options if not provided
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: CAIRO_TIMEZONE,
      hour12: true, // Always use 12-hour format for all locales
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    };

    return date.toLocaleString(localeString, defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error, utcString);
    return '-';
  }
}

/**
 * Format date only (no time) in Cairo timezone
 */
export function formatCairoDate(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en'
): string {
  return formatCairoTime(utcString, locale, {
    timeZone: CAIRO_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format time only (no date) in Cairo timezone
 */
export function formatCairoTimeOnly(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en'
): string {
  return formatCairoTime(utcString, locale, {
    timeZone: CAIRO_TIMEZONE,
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format datetime with full details (day name, date, time)
 */
export function formatCairoDateTimeFull(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en'
): string {
  return formatCairoTime(utcString, locale, {
    timeZone: CAIRO_TIMEZONE,
    hour12: true,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "5 minutes ago", "قبل 5 دقائق")
 * @param utcString - UTC datetime string from backend
 * @param locale - Current language locale
 * @param t - Translation function from react-i18next
 * @returns Localized relative time string
 */
export function formatRelativeTime(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en',
  t?: TranslationFunction
): string {
  if (!utcString || utcString.trim() === '') {
    return '-';
  }

  try {
    // Ensure UTC parsing by appending 'Z' if not present
    let isoString = utcString.trim();
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString += 'Z';
    }
    
    // Validate UTC date
    const utcDate = new Date(isoString);
    if (isNaN(utcDate.getTime())) {
      return '-';
    }

    // Convert both times to Cairo timezone before calculation
    // This ensures no UTC/browser timezone mismatch
    const nowInCairo = getCurrentCairoTime();
    const dateInCairo = convertUtcToCairo(utcString);
    
    // Calculate difference using Cairo timezone for BOTH dates
    const diffMs = nowInCairo.getTime() - dateInCairo.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Use translations if available, otherwise fallback
    if (diffYears > 0) {
      if (locale === 'ar') {
        return t ? t('time.yearsAgo', { count: diffYears }) : `قبل ${diffYears} ${diffYears === 1 ? 'سنة' : 'سنوات'}`;
      }
      return t ? t('time.yearsAgo', { count: diffYears }) : `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
    }
    
    if (diffMonths > 0) {
      if (locale === 'ar') {
        return t ? t('time.monthsAgo', { count: diffMonths }) : `قبل ${diffMonths} ${diffMonths === 1 ? 'شهر' : 'شهور'}`;
      }
      return t ? t('time.monthsAgo', { count: diffMonths }) : `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
    }
    
    if (diffWeeks > 0) {
      if (locale === 'ar') {
        return t ? t('time.weeksAgo', { count: diffWeeks }) : `قبل ${diffWeeks} ${diffWeeks === 1 ? 'أسبوع' : 'أسابيع'}`;
      }
      return t ? t('time.weeksAgo', { count: diffWeeks }) : `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
    }
    
    if (diffDays > 0) {
      if (locale === 'ar') {
        return t ? t('time.daysAgo', { count: diffDays }) : `قبل ${diffDays} ${diffDays === 1 ? 'يوم' : 'أيام'}`;
      }
      return t ? t('time.daysAgo', { count: diffDays }) : `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }
    
    if (diffHours > 0) {
      if (locale === 'ar') {
        return t ? t('time.hoursAgo', { count: diffHours }) : `قبل ${diffHours} ${diffHours === 1 ? 'ساعة' : 'ساعات'}`;
      }
      return t ? t('time.hoursAgo', { count: diffHours }) : `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }
    
    if (diffMinutes > 0) {
      if (locale === 'ar') {
        return t ? t('time.minutesAgo', { count: diffMinutes }) : `قبل ${diffMinutes} ${diffMinutes === 1 ? 'دقيقة' : 'دقائق'}`;
      }
      return t ? t('time.minutesAgo', { count: diffMinutes }) : `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    }
    
    if (locale === 'ar') {
      return t ? t('time.justNow', 'الآن') : 'الآن';
    }
    return t ? t('time.justNow', 'Just now') : 'Just now';
    
  } catch (error) {
    console.error('Error formatting relative time:', error, utcString);
    return '-';
  }
}

/**
 * Format date for forms/inputs (YYYY-MM-DD)
 */
export function formatDateForInput(
  utcString: string | null | undefined
): string {
  if (!utcString || utcString.trim() === '') {
    return '';
  }

  try {
    // Ensure UTC parsing by appending 'Z' if not present
    let isoString = utcString.trim();
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString += 'Z';
    }
    
    const date = new Date(isoString);
    
    if (isNaN(date.getTime())) {
      return '';
    }

    // Convert to Cairo timezone and format as YYYY-MM-DD
    const year = date.toLocaleString('en-US', { timeZone: CAIRO_TIMEZONE, year: 'numeric' });
    const month = date.toLocaleString('en-US', { timeZone: CAIRO_TIMEZONE, month: '2-digit' });
    const day = date.toLocaleString('en-US', { timeZone: CAIRO_TIMEZONE, day: '2-digit' });
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error, utcString);
    return '';
  }
}

/**
 * Check if date is today in Cairo timezone
 */
export function isToday(utcString: string | null | undefined): boolean {
  if (!utcString || utcString.trim() === '') {
    return false;
  }

  try {
    // Ensure UTC parsing by appending 'Z' if not present
    let isoString = utcString.trim();
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString += 'Z';
    }
    
    const date = new Date(isoString);
    const now = new Date();
    
    const dateInCairo = date.toLocaleString('en-US', { 
      timeZone: CAIRO_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const nowInCairo = now.toLocaleString('en-US', { 
      timeZone: CAIRO_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    return dateInCairo === nowInCairo;
  } catch (error) {
    return false;
  }
}

/**
 * Format datetime for display in tables and lists
 * Shows date and time in compact format
 */
export function formatCompactDateTime(
  utcString: string | null | undefined,
  locale: 'ar' | 'en' = 'en'
): string {
  return formatCairoTime(utcString, locale, {
    timeZone: CAIRO_TIMEZONE,
    hour12: true,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get Cairo timezone offset string (e.g., "UTC+2")
 */
export function getCairoOffset(): string {
  const now = new Date();
  const cairoTime = new Date(now.toLocaleString('en-US', { timeZone: CAIRO_TIMEZONE }));
  const offsetMinutes = (cairoTime.getTime() - now.getTime()) / (1000 * 60);
  const offsetHours = offsetMinutes / 60;
  
  if (offsetHours >= 0) {
    return `UTC+${offsetHours}`;
  }
  return `UTC${offsetHours}`;
}

// Backward compatibility aliases (deprecated - use formatCairoTime instead)
export const formatEgyptTime = formatCairoTime;
export const formatEgyptDateTime = formatCairoTime;

export default {
  // Core conversion functions
  convertUtcToCairo,
  getCurrentCairoTime,
  // Formatting functions
  formatCairoTime,
  formatCairoDate,
  formatCairoTimeOnly,
  formatCairoDateTimeFull,
  formatRelativeTime,
  formatDateForInput,
  formatCompactDateTime,
  // Utility functions
  isToday,
  getCairoOffset,
  // Backward compatibility
  formatEgyptTime,
  formatEgyptDateTime,
};
